import React, {Component} from "react";
import {connect} from 'react-redux';

import OverviewMap from '../../../custom_libs/OverviewMap';
import TileLayer from "ol/layer/Tile";
import TileImage from 'ol/source/TileImage';
import TileGrid from 'ol/tilegrid/TileGrid';
import {addToVisitedList, getColorLayer} from '../../neoviewer/apps/preview_coloring';
import {showSeenAreaChanged} from "../../../redux_actions/viewerSetting.action";

import "../../../asset/style/neoviewer/preview.css"
import {getPrefixedUrlSecure, isNull, notNull} from "../../../utils/utils";
import {Stack, Tooltip} from "@mui/material";
import {HorizontalDivider} from "../components/components";
import {StyledSwitch} from "../components/StyledComponents";
import {getResolutions, getTileSize, getZoomLevels, imageLoader} from "../utils/map_utils";
import {Loader, responsiveLayers} from "../../../utils/const";
import {notifyInfo} from "../utils/display.notification";

class PreviewMap extends Component {
    constructor(props) {
        super(props);

        this.state = this.initState()
        document.addEventListener("keydown", this.preventTab);

        this.isMobile = document.documentElement.clientWidth <= responsiveLayers.MOBILE_WIDTH_LEVEL &&
            (document.documentElement.clientWidth < document.documentElement.clientHeight);
    }

    /** Under OpenSeadragon the OL OverviewMap cannot attach (OSD addControl needs an anchor); stay idle. */
    isOsdViewer = () => this.props.mapState?.slidemap?._isOpenSeadragon === true;

    initState = () => {
        if (notNull(this.props.mapState?.slide) && notNull(this.props.mapState?.slidemap) && !this.isOsdViewer()) {
            this.initResolutions();
            this.initPreviewMap();
            this.initZoomVisitedMap();
            this.initColorLayer();

            this.props.mapState.slidemap.on(['click', 'singleclick', 'moveend'], this.onVisitEvent);
        }
        return {
            previewColouring: false, initialized: false,
            isRotated: (this.props.displaySetting?.rotation % 360) > 0
        };
    }

    initResolutions = () => {
        this.resolutions = getResolutions(getZoomLevels(this.props.mapState?.slide));
    }

    initPreviewMap = () => {
        this.previewMap = new OverviewMap({
            layers: [
                new TileLayer({
                    source: new TileImage({
                        tileGrid: new TileGrid({
                            extent: this.props.mapState.slidemap.getView().getProjection().getExtent(),
                            origin: [0, this.props.mapState.slidemap.getView().getProjection().getExtent()[3]],
                            resolutions: this.resolutions,
                            tileSize: getTileSize(this.props.mapState.slide),
                        }),
                        projection: this.props.mapState.slidemap.getView().getProjection(),
                        url: getPrefixedUrlSecure(`stitched/{z}/x{x}y{y}.jpg`,
                            this.props.mapState.slide),
                        tileLoadFunction: (image, src) => imageLoader(image, src, this.props.mapState?.slide?.id, this.props.mapState?.id),
                    })
                }),
            ],
            rotateWithView: true,
            collapsible: false,
            collapsed: false,
        });
    }

    initZoomVisitedMap = () => {
        this.zoomVisitedMap = {};
        this.zoomVisitedFullMap = {};

        this.props.mapState.zoomLevels.forEach((level) => {
            this.zoomVisitedMap[parseInt(level)] = [];
        });
    }

    initColorLayer = () => {
        this.colorLayer = getColorLayer(this.previewMap, this.props.mapState.slidemap.getView(), this.zoomVisitedMap);
        this.previewMap.getOverviewMap().addLayer(this.colorLayer);
    }


    onVisitEvent = () => {
        if (this.state.previewColouring) {
            addToVisitedList(this.props.mapState.slidemap.getView(), this.zoomVisitedFullMap, this.zoomVisitedMap);
            this.colorLayer.getSource().changed();
        }
    }

    mountPreviewMap = () => {
        if (!this.previewMap || this.isOsdViewer()) return;
	    this.previewMap.setTarget('preview-map');
	    this.props.mapState.slidemap.addControl(this.previewMap);
    }

    unmountPreviewMap = () => {
        if (!this.previewMap) return;
        this.previewMap.setTarget(null);
        let e = document.getElementById("preview-map");
        if (e?.lastChild) {
            for (let previewMap = e.lastChild; previewMap; previewMap = e.lastChild)
                e.removeChild(previewMap)
        }
        this.previewMap = null;
    }

    preventTab = (e) => {
        if (e.keyCode === 9)
            e.preventDefault()
    }

    componentDidMount() {
        // Verification of Code as MORPHLE is necessary else, the preview map is created before extent is set,
        // resulting in incorrect map
        if (!this.isOsdViewer() && this.props.mapState?.slidemap?.getView()?.getProjection()?.getCode() === "MORPHLE") {
            this.unmountPreviewMap();
            this.setState(this.initState());
            this.mountPreviewMap();
            this.setState({initialized: true});
        }
        if (this.state.isRotated) {
            notifyInfo("previewMap", "Show Seen Area works for scan rotation zero")
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.isOsdViewer()) {
            this.unmountPreviewMap();
            return;
        }

        if (isNull(prevProps.mapState?.slide) && notNull(this.props.mapState?.slide) && isNull(prevProps.mapState?.slidemap)
            && notNull(this.props.mapState?.slidemap)) {
            this.initState();
        }

        if (!this.props.mapState || !prevProps.mapState || this.props.mapState?.slidemap?.getView()?.getProjection()?.getCode() !== "MORPHLE") return;
        if ((!this.state.initialized && this.props.mapState?.slidemap?.getView()?.getProjection()?.getCode() === "MORPHLE") ||
            (prevProps.activeMapId !== this.props.activeMapId || prevProps.lastMapCount !== this.props.lastMapCount)) {
            this.unmountPreviewMap();
            this.setState(this.initState());
            this.mountPreviewMap();
            this.setState({initialized: true});
        }
        if (prevProps.displaySetting?.rotation !== this.props.displaySetting?.rotation && notNull(this.props.displaySetting?.rotation)) {
            this.setState({isRotated: (this.props.displaySetting?.rotation % 360) > 0});
            notifyInfo("previewMap", "Show Seen Area in Preview works when scan rotation is zero")
            if ((this.props.displaySetting?.rotation % 360) > 0) {
                this.setState(this.initState());
            }
        }
    }

    switchPreviewColoring = (value) => {
        this.setState({
            previewColouring: value
        })
        this.props.dispatch(showSeenAreaChanged());
    }

    render() {
        return <Stack height={"100%"} direction={"column"} justifyContent={"space-between"}
                      alignItems={"center"}>
            <div id={"preview-map"} style={{
                height: "78%",
                width: "100%",
                backgroundColor: 'whitesmoke',
                display: this.props.loadingState.loading ? "none" : "block"
            }}/>
            {this.props.loadingState.loading && <Loader/>}
            {!this.isMobile ? <Stack sx={{height: "22%", width: "100%"}}>
                <HorizontalDivider sx={{my: 0.5}}/>
                <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} spacing={1}>
                    Show Seen Area
                    <Tooltip title={this.state.isRotated ? "Works when scan rotation is zero" : ""} placement="top"
                             value={this.state.isRotated && this.state.previewColouring}>
                        <span>
                            <StyledSwitch
                                color={'secondary'}
                                checked={this.state.previewColouring}
                                disabled={this.state.isRotated}
                                onChange={(e) => this.switchPreviewColoring(e.target.checked)}
                            />
                        </span>
                    </Tooltip>
                </Stack>
            </Stack> : null}
        </Stack>

    }
}

const mapStateToProps = (state) => {
    let loadingState = state.slideLoadingReducer;
    let {activeMapId, lastMapCount} = state.gammaStateReducer;
    let mapState = state.mapsStateReducer[activeMapId] ?? {};
    let {displaySetting} = mapState;
    return {activeMapId, lastMapCount, mapState, displaySetting, loadingState};
}

export default connect(mapStateToProps)(PreviewMap);
